import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";

import { useIsGreek } from "../context/LanguageContext";
import { fetchStringLayout } from "../services/api";
import { planStringAssignments } from "../utils/planStringAssignments";

/**
 * Which string sits on which row -- drawn, checked, and editable by hand.
 *
 * THE JOIN THE PLANT NEVER HAD
 * ----------------------------
 * `systemDesign` is an electrical tree; the field layout is a geometric count.
 * They were typed separately and agreed by coincidence. This map is the one
 * place both are read together: the server fills the rows with the strings in
 * electrical order (pv_core/string_layout.py -- the browser asks, it does not
 * repeat the rule), colours each segment by inverter, and prints a check that
 * says whether the string modules and the row positions are the same plant.
 *
 * Editing is done on the picture itself. A click picks a string up; a click on
 * a free stretch of a row drops it there, a click on another string swaps the
 * two. Nobody knows that a string belongs at column 27 -- they know it belongs
 * in that row after that one -- so there is no dialog asking for a column. A
 * hand placement is a constraint the automatic fill works around; one that
 * cannot be honoured is reported, not silently obeyed at another string's
 * expense.
 */

/**
 * Where a string of `width` columns lands in a row when dropped at `at`.
 *
 * The free stretches of the row are found (ignoring the string's own current
 * place, if it is already on this row); the one under the pointer wins if it
 * is wide enough, else the first wide enough anywhere in the row; the string
 * is put as close to the pointer as the stretch allows. Null when nothing is
 * wide enough -- the drop is then refused rather than made into a collision.
 */
export function dropColumn(row, width, at, movingId = null) {
  const taken = (row.segments || [])
    .filter((seg) => seg.string_id !== movingId)
    .map((seg) => [seg.column, seg.column + seg.columns])
    .sort((a, b) => a[0] - b[0]);
  const gaps = [];
  let cursor = 0;
  taken.forEach(([a, b]) => {
    if (a > cursor) gaps.push([cursor, a]);
    cursor = Math.max(cursor, b);
  });
  if (cursor < row.columns) gaps.push([cursor, row.columns]);
  const fits = gaps.filter(([a, b]) => b - a >= width);
  if (!fits.length) return null;
  const under = fits.find(([a, b]) => at >= a && at < b);
  const [a, b] = under || fits[0];
  return Math.max(a, Math.min(Math.round(at - width / 2), b - width));
}

// Eight hues a colour-blind reader can still separate, used in this order.
const INVERTER_HUES = [
  "#3b6fb6", "#c2751a", "#4a8f5c", "#9b4f96",
  "#c94c4c", "#2f8f9d", "#8a7a2b", "#6b6b6b",
];

const lighten = (hex, amount) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

export default function StringLayoutMap({
  systemDesign,
  rowModules,            // module columns per table
  rowLabels = null,      // optional labels ("R3a")
  tiers = 1,
  overrides = null,      // {string_id: {row, column}}
  onOverridesChange = null,
  accessToken = null,
  allowCrossRows = false,
  onLayoutChange = null,
  excludedSlots = null,  // ["row:column:tier"] the plan holds no module in
  assignments = null,    // frozen links while the plan and design are reconciled
}) {
  const greek = useIsGreek();
  const tr = (el, en) => greek ? el : en;
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [layout, setLayout] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);       // the string being moved
  const [notice, setNotice] = useState("");         // why a drop was refused
  const timer = useRef(null);

  const rows = useMemo(
    () => (Array.isArray(rowModules) ? rowModules.map((c) => Number(c)).filter((c) => c > 0) : []),
    [rowModules]
  );
  const hasInputs = rows.length > 0 && systemDesign && Object.keys(systemDesign).length > 0;
  const requestKey = JSON.stringify({ systemDesign, allowCrossRows, rowModules: rows,
    rowLabels: rowLabels || undefined, tiers: Number(tiers) || 1, overrides: overrides || undefined,
    excludedSlots: Array.isArray(excludedSlots) && excludedSlots.length ? [...excludedSlots].sort() : undefined });
  const assignmentKey = assignments == null ? null : JSON.stringify(assignments);
  const manualLayout = useMemo(() => {
    if (assignmentKey == null) return null;
    const request = JSON.parse(requestKey);
    return planStringAssignments({ ...request, assignments: JSON.parse(assignmentKey) });
  }, [requestKey, assignmentKey]);
  const layoutListener = useRef(onLayoutChange);
  useEffect(() => { layoutListener.current = onLayoutChange; }, [onLayoutChange]);

  useEffect(() => {
    if (manualLayout) {
      setLayout(manualLayout);
      setBusy(false);
      setError("");
      setPicked(null);
      layoutListener.current?.(manualLayout);
      return undefined;
    }
    setLayout(null);
    if (layoutListener.current) layoutListener.current(null);
    if (!hasInputs) return undefined;
    // A short debounce: the row list changes on every keystroke in the layout
    // form, and the fill is cheap on the server but not free on the wire.
    clearTimeout(timer.current);
    let cancelled = false;
    timer.current = setTimeout(async () => {
      setBusy(true);
      setError("");
      try {
        const out = await fetchStringLayout(JSON.parse(requestKey), accessToken);
        if (!cancelled) { setLayout(out); if (layoutListener.current) layoutListener.current(out); }
      } catch (e) {
        if (!cancelled) setError(e?.message || "The strings could not be placed.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [hasInputs, requestKey, accessToken, manualLayout]);

  useEffect(() => {
    if (!picked) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setPicked(null); setNotice(""); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [picked]);

  const hueFor = (inverter) => {
    const base = INVERTER_HUES[(Number(inverter) - 1) % INVERTER_HUES.length];
    return dark ? lighten(base, 0.25) : base;
  };

  if (!hasInputs) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        The string map needs a saved electrical design and a row count. Design the
        strings under Equipment, then set the rows here or fit them to the parcel.
      </Typography>
    );
  }

  const maxColumns = Math.max(...rows);
  const check = layout?.check;
  const problems = layout?.problems || [];
  const lengths = [...new Set((layout?.strings || layout?.rows?.flatMap(r => r.segments) || []).map(s => s.modules))];
  const stringModules = lengths.length === 1 ? Number(lengths[0]) : null;
  const stringColumns = stringModules ? Math.ceil(stringModules / Math.max(1, Number(tiers))) : null;
  const wholeStringCapacity = stringColumns ? rows.reduce((sum, n) => sum + Math.floor(n / stringColumns), 0) : null;
  const placementExplanation = !allowCrossRows && check?.unplaced_strings > 0 ? (stringColumns
    ? tr(`Κάθε string ${stringModules} πλαισίων δεσμεύει ${stringColumns} ολόκληρες στήλες των ${tiers} θέσεων. Οι τρέχουσες σειρές χωρούν έως ${wholeStringCapacity} τέτοια strings πριν από χειροκίνητες δεσμεύσεις. Οι υπόλοιπες θέσεις είναι κατανεμημένες σε διαφορετικές σειρές ή δεσμευμένες στήλες. Αλλάξτε τα μήκη/τον αριθμό σειρών ή επιλέξτε «Συνέχεια μεταξύ σειρών».`,
      `Each ${stringModules}-module string reserves ${stringColumns} whole columns of ${tiers} positions. The current rows fit at most ${wholeStringCapacity} such strings before manual reservations. Remaining positions are split between rows or reserved columns. Change row lengths/count or choose “Continue across rows”.`)
    : tr("Οι ελεύθερες θέσεις δεν σχηματίζουν επαρκείς συνεχόμενες στήλες για ολόκληρα strings σε μία σειρά. Αλλάξτε μήκη/αριθμό σειρών, ελέγξτε τις χειροκίνητες θέσεις ή επιλέξτε συνέχεια μεταξύ σειρών.",
      "Free positions do not form enough contiguous columns for complete strings on single rows. Change row lengths/count, check manual positions or allow strings across rows.")) : null;

  const widthOf = (string) => Math.ceil(Number(string.modules) / (Number(tiers) || 1));
  const writeOverrides = (next) => onOverridesChange(Object.keys(next).length ? next : null);

  /** A click on a string: pick it up, put it down, or swap with the one picked. */
  const clickString = (seg, rowNumber) => {
    if (!onOverridesChange) return;
    setNotice("");
    const here = { ...seg, ...(layout?.strings || []).find((s) => s.string_id === seg.string_id), row: rowNumber };
    if (!picked) { setPicked(here); return; }
    if (picked.string_id === seg.string_id) { setPicked(null); return; }
    // Swap: each takes the other's place. Different widths may leave the
    // wider one overlapping a neighbour -- the check then says so.
    const next = { ...(overrides || {}) };
    if (here.row != null) next[picked.string_id] = { row: here.row, column: here.column };
    else delete next[picked.string_id];
    if (picked.row != null) next[seg.string_id] = { row: picked.row, column: picked.column };
    else delete next[seg.string_id];
    writeOverrides(next);
    setPicked(null);
  };

  /** A click on a row's bar, away from any string: drop the picked string there. */
  const clickRow = (row, event) => {
    if (!picked || !onOverridesChange) return;
    const box = event.currentTarget.getBoundingClientRect();
    const at = box.width > 0 ? ((event.clientX - box.left) / box.width) * row.columns : 0;
    const width = widthOf(picked);
    const column = allowCrossRows ? Math.max(0, Math.min(row.columns - 1, Math.floor(at))) : dropColumn(row, width, at, picked.string_id);
    if (column == null) {
      setNotice(tr(`${row.label || `R${row.row}`}: δεν υπάρχει ελεύθερο τμήμα ${width} στηλών για το ${picked.string_id}.`, `${row.label || `R${row.row}`} has no free stretch of ${width} columns for ${picked.string_id}.`));
      return;
    }
    writeOverrides({ ...(overrides || {}), [picked.string_id]: { row: row.row, column } });
    setPicked(null);
    setNotice("");
  };

  const releasePicked = () => {
    if (!picked || !onOverridesChange) return;
    const next = { ...(overrides || {}) };
    delete next[picked.string_id];
    writeOverrides(next);
    setPicked(null);
  };

  const unplaced = (layout?.strings || []).filter((st) => st.row == null);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }} useFlexGap>
        {check ? (
          <Chip
            size="small"
            color={check.consistent ? "success" : "warning"}
            variant={check.consistent ? "filled" : "outlined"}
            label={layout.manualPlan ? tr(
              `${check.unplaced_strings} ελλιπή strings · ${check.empty_slots} πάνελ χωρίς string`,
              `${check.unplaced_strings} incomplete strings · ${check.empty_slots} modules without a string`)
              : greek ? (check.consistent ? `${check.placed_modules} πλαίσια αντιστοιχισμένα σε strings` : `${check.unplaced_strings} strings χωρίς θέση · ${check.empty_slots} ελεύθερες θέσεις`) : layout.description}
          />
        ) : busy ? (
          <Chip size="small" label={tr("Αντιστοίχιση strings…", "Placing strings…")} />
        ) : null}
        <Box sx={{ flex: 1 }} />
        {(layout?.inverters || []).map((inv) => (
          <Stack key={inv} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: hueFor(inv) }} />
            <Typography variant="caption">INV-{String(inv).padStart(2, "0")}</Typography>
          </Stack>
        ))}
        {overrides && Object.keys(overrides).length > 0 && onOverridesChange ? (
          <Button size="small" color="warning" onClick={() => onOverridesChange(null)} sx={{ textTransform: "none" }}>
            {tr(`Αυτόματη αντιστοίχιση strings (${Object.keys(overrides).length})`, `Reset ${Object.keys(overrides).length} string placements to automatic`)}
          </Button>
        ) : null}
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {layout?.manualPlan && !layout.check.consistent && <Alert severity="warning" sx={{ mb: 1 }}>
        {layout.strings.filter(s => s.missing_modules).map(s => <Typography key={s.string_id} variant="body2">
          {s.string_id}: {tr(`ελλιπές — ${s.present_modules}/${s.modules} πάνελ`, `incomplete — ${s.present_modules}/${s.modules} modules`)}
        </Typography>)}
        {layout.unassigned.length > 0 && <Typography variant="body2">{tr(
          `${layout.unassigned.length} πάνελ χωρίς string. Ενημερώστε τον ηλεκτρολογικό σχεδιασμό από την κάτοψη.`,
          `${layout.unassigned.length} modules without a string. Update the electrical design from the plan.`)}</Typography>}
      </Alert>}

      {picked ? (
        <Alert
          severity="info"
          icon={false}
          sx={{ mb: 1, py: 0.25, alignItems: "center" }}
          action={
            <Stack direction="row" spacing={0.5}>
              {overrides?.[picked.string_id] ? (
                <Button size="small" color="inherit" onClick={releasePicked} sx={{ textTransform: "none" }}>
                  {tr("Επιστροφή στην αυτόματη θέση", "Back to automatic")}
                </Button>
              ) : null}
              <Button size="small" color="inherit" onClick={() => { setPicked(null); setNotice(""); }} sx={{ textTransform: "none" }}>
                {tr("Ακύρωση", "Cancel")}
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2">
            {tr("Μετακίνηση", "Moving")} <b>{picked.string_id}</b> ({widthOf(picked)} {tr("στήλες", "columns")}): {tr("κάντε κλικ σε ελεύθερο τμήμα σειράς για τοποθέτηση ή σε άλλο string για ανταλλαγή θέσεων. Esc: ακύρωση.", "click a free stretch of a row to put it there, or another string to swap the two. Esc cancels.")}
          </Typography>
          {notice ? <Typography variant="body2" sx={{ color: "warning.main" }}>{notice}</Typography> : null}
        </Alert>
      ) : null}

      {/* One bar per table, to a common scale, so a short table reads short. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", rowGap: 0.5, columnGap: 1, alignItems: "center" }}>
        {(layout?.rows || rows.map((c, i) => ({ row: i + 1, columns: c, segments: [] }))).map((row) => (
          <React.Fragment key={row.row}>
            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 34 }}>
              {row.label || rowLabels?.[row.row - 1] || `R${row.row}`}
            </Typography>
            <Box
              data-row={row.row}
              onClick={(e) => clickRow(row, e)}
              sx={{
                position: "relative", height: 22, borderRadius: 0.75,
                bgcolor: "action.hover", border: "1px solid", borderColor: "divider",
                width: `${(row.columns / maxColumns) * 100}%`,
                cursor: picked ? "copy" : "default",
                ...(picked ? { "&:hover": { borderColor: "primary.main", bgcolor: "action.selected" } } : {}),
              }}
            >
              {(row.segments || []).map((seg) => {
                const left = (seg.column / row.columns) * 100;
                const width = (seg.columns / row.columns) * 100;
                const label = `MPPT ${seg.mppt} · IN ${seg.input}`;
                return (
                  <Tooltip
                    key={`${seg.string_id}-${seg.column}`}
                    arrow
                    title={`${seg.string_id} — ${seg.modules} ${tr("πλαίσια σε αυτό το τμήμα", "modules in this segment")} · ${row.label || rowLabels?.[row.row - 1] || `R${row.row}`} · ${tr("θέσεις πάνελ", "module slots")} ${Math.round(seg.column * tiers) + 1}–${Math.round((seg.column + seg.columns) * tiers)}${seg.incomplete ? tr(" · Ελλιπές string", " · Incomplete string") : ""}${seg.manual ? tr(" · Θέση εκκίνησης ορισμένη από τον χρήστη", " · Starting position set by the user") : ""}`}
                  >
                    <Box
                      onClick={(e) => { e.stopPropagation(); clickString(seg, row.row); }}
                      sx={{
                        position: "absolute", top: 1, bottom: 1,
                        left: `${left}%`, width: `calc(${width}% - 2px)`, ml: "1px",
                        bgcolor: seg.incomplete ? "warning.dark" : hueFor(seg.inverter), borderRadius: 0.5,
                        outline: picked?.string_id === seg.string_id
                          ? `2px solid ${theme.palette.warning.main}`
                          : seg.manual ? `2px solid ${theme.palette.text.primary}` : "none",
                        outlineOffset: -2,
                        opacity: picked && picked.string_id !== seg.string_id ? 0.8 : 1,
                        boxShadow: picked?.string_id === seg.string_id ? `0 0 0 3px ${theme.palette.warning.light}` : "none",
                        cursor: onOverridesChange ? (picked ? "alias" : "grab") : "default",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden",
                        "&:hover": { filter: "brightness(1.08)" },
                      }}
                    >
                      {width > 9 ? (
                        <Typography variant="caption" sx={{ color: "#fff", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", px: 0.5, textShadow: "0 0 2px rgba(0,0,0,.6)" }}>
                          {label}
                        </Typography>
                      ) : null}
                    </Box>
                  </Tooltip>
                );
              })}
              {(row.unassigned || []).map(p => <Box key={`free-${p.column}-${p.tier}`} title={tr("Χωρίς string", "Without a string")}
                sx={{ position: "absolute", left: `${(p.column + p.tier / tiers) / row.columns * 100}%`, width: `${100 / (row.columns * tiers)}%`,
                  top: 0, bottom: 0, bgcolor: "warning.light", border: "1px dashed", borderColor: "warning.dark" }} />)}
              {(row.excluded || []).map(slot => {
                const [c, t] = Array.isArray(slot) ? slot : slot.split(":").slice(1).map(Number);
                return <Box key={slot} title={tr("Κενή θέση πλαισίου", "Empty module slot")} sx={{ position: "absolute",
                  left: `${(c + t / tiers) / row.columns * 100}%`, width: `${100 / (row.columns * tiers)}%`, top: 0, bottom: 0,
                  bgcolor: "background.paper", border: "1px dashed", borderColor: "error.main", pointerEvents: "none" }} />;
              })}
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
              {row.columns} <span>{tr("στήλες", "col")}</span>
              {Number(row.empty_columns) > 1e-6 ? <> · {Number(Number(row.empty_columns).toFixed(3))} <span>{tr("ελεύθερες", "free")}</span></> : null}
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      {/* Strings with no row yet: on a shelf, so they can be picked up and
          dropped where there is room, like any other. */}
      {unplaced.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 34 }}>{tr("Χωρίς θέση", "Not placed")}</Typography>
          {unplaced.map((st) => (
            <Tooltip key={st.string_id} arrow title={`${st.string_id} — ${st.modules} ${tr("πλαίσια, δεν υπάρχει αρκετός χώρος σε σειρά", "modules, no row has room")}`}>
              <Box
                onClick={() => clickString({ ...st, column: null, columns: widthOf(st), manual: false }, null)}
                sx={{
                  px: 1, height: 20, borderRadius: 0.5, bgcolor: hueFor(st.inverter),
                  color: "#fff", fontSize: 10, fontWeight: 600, display: "flex", alignItems: "center",
                  cursor: onOverridesChange ? "grab" : "default",
                  outline: picked?.string_id === st.string_id ? `2px solid ${theme.palette.warning.main}` : "none",
                }}
              >
                {`MPPT ${st.mppt} · IN ${st.input}`}
              </Box>
            </Tooltip>
          ))}
        </Stack>
      ) : null}

      {/* The reason, before the list. A dozen "does not fit" lines say the same
          thing twelve times; the hint says why once and what would fix it. */}
      {placementExplanation || layout?.hint ? (
        <Alert severity="info" sx={{ mt: 1.25 }}>
          <Typography variant="body2">{placementExplanation || (greek
            ? "Ελέγξτε τις διαθέσιμες συνεχόμενες θέσεις ανά σειρά και τις χειροκίνητες αντιστοιχίσεις των strings."
            : layout.hint)}</Typography>
        </Alert>
      ) : null}
      {problems.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2">{tr(`${problems.length} προβλήματα τοποθέτησης. Η αντιστοίχιση δεν είναι ολοκληρωμένη.`, `${problems.length} placement issues. Mapping is incomplete.`)}</Typography>
          <Box component="details" sx={{ mt: 0.5 }}><summary>{tr("Ποια strings χρειάζονται διόρθωση", "Strings requiring attention")}</summary>
          {problems.map((p) => (
            <Typography key={p.string_id + p.kind} variant="body2">
              <b>{p.string_id}</b>: {greek ? (p.kind === "unplaced" ? "Δεν επαρκούν οι διαθέσιμες θέσεις για αυτό το string." : "Η χειροκίνητη θέση δεν είναι διαθέσιμη. Ελέγξτε την αυτόματη τοποθέτηση.") : p.detail}
            </Typography>
          ))}
          </Box>
        </Alert>
      ) : null}

      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
        {tr("Κάθε χρωματιστό τμήμα είναι ένα string ή μέρος του. Το χρώμα δείχνει τον αντιστροφέα. Το σκούρο περίγραμμα δηλώνει θέση εκκίνησης που ορίσατε στον χάρτη strings· δεν μετακινεί πάνελ στην κάτοψη.",
          "Each coloured segment is a string or part of one. Colour identifies the inverter. A dark outline marks a starting position you set in the string map; it does not move modules in the plan.")}
        {" "}{tr(`Κάθε στήλη έχει ${tiers} πάνελ καθ’ ύψος. Οι θέσεις στο hover μετρούν τα πάνελ διαδοχικά, ανά στήλη και επίπεδο.`,
          `Each column has ${tiers} modules vertically. Hover slot numbers count modules consecutively, by column and tier.`)}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
        {allowCrossRows
          ? tr("Τα strings ακολουθούν τις θέσεις πλαισίων κατά στήλη και επίπεδο και μπορούν να συνεχίζονται σε άλλη συστοιχία. Επιλέξτε string και κατόπιν θέση εκκίνησης. Ελέγξτε τη φυσική διαδρομή και τα μήκη καλωδίων.", "Strings follow module positions by column and tier and can continue on another table. Select a string, then its starting position. Check the physical cable route and lengths.")
          : tr("Κάθε string καταλαμβάνει μία συστοιχία. Επιλέξτε string και κατόπιν ελεύθερη θέση ή άλλο string για ανταλλαγή.", "Each string occupies one table. Select a string, then a free position or another string to swap.")}

      </Typography>

    </Box>
  );
}
